import { createContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import type { User } from "../types/user";
import { authAPI, APIError } from "../lib/api";
import { setSentryUser, clearSentryUser } from "../lib/sentry";

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  email: string;
  password: string;
  organizationName?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  refetchUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await authAPI.me();
      setUser(response.user);
      setSentryUser(response.user.id, response.user.email);
    } catch (error) {
      // Clear user state on 401 (not authenticated)
      if (error instanceof APIError && error.statusCode === 401) {
        setUser(null);
        clearSentryUser();
      } else {
        console.error("Failed to fetch current user:", error);
        setUser(null);
        clearSentryUser();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = useCallback(async (credentials: LoginCredentials): Promise<User> => {
    try {
      const response = await authAPI.login(credentials);
      setUser(response.user);
      setSentryUser(response.user.id, response.user.email);
      return response.user;
    } catch (error) {
      // Handle 401 errors by clearing user state
      if (error instanceof APIError && error.statusCode === 401) {
        setUser(null);
        clearSentryUser();
      }
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      clearSentryUser();
      window.location.href = "/";
    }
  }, []);

  const register = useCallback(async (data: RegisterData): Promise<void> => {
    await authAPI.register(data);
    // Don't set user state - requires email verification first
  }, []);

  const refetchUser = useCallback(async (): Promise<void> => {
    setLoading(true);
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      loading,
      login,
      logout,
      register,
      refetchUser,
    }),
    [user, loading, login, logout, register, refetchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
